import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import "./guest.css";
import PasswordChangeForm from "./PasswordChangeForm";
import { allowSignedIn, locallySignedOut, markSignedOut, remoteSignOutPending, useSessionTermination } from "./sessionTermination";
import { startBridgeCall, type BridgeCallProblem, type BridgeCallStage } from "./carerConnectCall";

// Issue #1053: the authorised-guest area for a Family caller (R15). It has its
// own minimal layout, never renders the staff console, and talks only to
// /api/guest. Same journey as staff (docs/120 v1.3): set up with a code texted
// to the registered mobile number, then sign in with mobile number, password
// and a texted code. The calling page is #1054; the call itself is #1055.

type Session = { expiresAt?: number; configured: boolean; authenticated: boolean; reason?: string; closedReason?: ClosedReason; guest?: { guestId: string; linkActive: boolean } };
type ClosedReason = "paused" | "consent-ended" | "ended";
// Issue #1054: only what docs/120 lets the caller see.
export type CallerPortal = {
  callerDisplayName: string | null;
  linkStatus: "active" | "setting-up";
  consentEndsOn: string | null;
  calling: { available: boolean; reason: "calling-not-ready" | "link-setting-up" | "unavailable-now" | null; modes?: "voice"[] };
};

const HELP = "If you need help, or you want to stop being able to call, contact the council team that set up your family calling.";
export const CLOSED_TEXT: Record<ClosedReason, string> = {
  paused: "Calling has been paused by the council while something is checked, so you cannot sign in for now. Contact the council team that set up your family calling if you have questions.",
  "consent-ended": "The permission for you to call has ended and needs to be renewed. Contact the council team that set up your family calling.",
  ended: "This family calling link has ended. Contact the council team that set up your family calling if you think this is wrong."
};
const longDate = (value: string) => new Date(`${value}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`/api/guest${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || "Something went wrong. Try again.");
  return result as T;
}

function useFocusOnChange<T extends HTMLElement>(key: unknown) {
  const ref = useRef<T>(null);
  useEffect(() => { ref.current?.focus(); }, [key]);
  return ref;
}

function Page({ title, children, accountControls }: { title: string; children: React.ReactNode; accountControls?: React.ReactNode }) {
  const heading = useFocusOnChange<HTMLHeadingElement>(title);
  useEffect(() => { document.title = `${title} – SmartProperties family calling`; }, [title]);
  return (
    <div className="guest-page">
      <header className="guest-header"><p className="guest-brand">SmartProperties family calling</p></header>
      <main className="guest-main" id="main">
        <h1 ref={heading} tabIndex={-1}>{title}</h1>
        {children}
        {accountControls}
      </main>
      <footer className="guest-footer"><p>If something is wrong, contact the council team that set up your family calling. Never share your password or codes with anyone, including council staff.</p></footer>
    </div>
  );
}

function ErrorText({ text }: { text: string }) {
  const ref = useFocusOnChange<HTMLParagraphElement>(text);
  return text ? <p className="guest-error" role="alert" ref={ref} tabIndex={-1}>{text}</p> : null;
}

function CodeField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <>
      <label htmlFor="guest-code">6-digit code</label>
      <input id="guest-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={value} onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))} />
    </>
  );
}

function PhoneField({ id, value, onChange, autoComplete }: { id: string; value: string; onChange: (value: string) => void; autoComplete: string }) {
  return (
    <>
      <label htmlFor={id}>Mobile number</label>
      <small id={`${id}-hint`}>The number the council registered for you, for example 07700 900000.</small>
      <input id={id} type="tel" inputMode="tel" autoComplete={autoComplete} required maxLength={20} aria-describedby={`${id}-hint`} value={value} onChange={(event) => onChange(event.target.value)} />
    </>
  );
}

export function GuestSetUpPage({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState(""), [requestId, setRequestId] = useState("");
  const [code, setCode] = useState(""), [password, setPassword] = useState(""), [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const request = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try { setRequestId((await post<{ requestId: string }>("/setup/request", { phone })).requestId); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "We could not send a code."); }
    finally { setBusy(false); }
  };
  const complete = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    if (password !== confirm) { setError("The two passwords are different. Type the same password twice."); return; }
    setBusy(true);
    try { await post("/setup/complete", { requestId, code, password }); onDone(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Your sign-in could not be set up."); }
    finally { setBusy(false); }
  };
  if (!requestId) {
    return (
      <Page title="Set up your sign-in">
        <p>The council has registered your mobile number so you can call a family member through SmartProperties. We will text a code to that number.</p>
        <ErrorText text={error} />
        <form onSubmit={request} className="guest-form">
          <PhoneField id="guest-setup-phone" value={phone} onChange={setPhone} autoComplete="tel" />
          <button type="submit" className="guest-button" disabled={busy}>{busy ? "Sending…" : "Text me a code"}</button>
        </form>
        <p>Already set up? <a href="/guest/sign-in">Sign in</a>.</p>
      </Page>
    );
  }
  return (
    <Page title="Enter your code and choose a password">
      <p className="guest-notice" role="status">If this number has been registered for family calling, we have sent a code to it. It works for 10 minutes.</p>
      <ErrorText text={error} />
      <form onSubmit={complete} className="guest-form">
        <CodeField value={code} onChange={setCode} />
        <label htmlFor="guest-new-password">New password</label>
        <small id="guest-password-hint">At least 12 characters. A few unrelated words works well. Do not use a password you use anywhere else.</small>
        <input id="guest-new-password" type="password" autoComplete="new-password" minLength={12} maxLength={128} required aria-describedby="guest-password-hint" value={password} onChange={(event) => setPassword(event.target.value)} />
        <label htmlFor="guest-confirm-password">Type the password again</label>
        <input id="guest-confirm-password" type="password" autoComplete="new-password" minLength={12} maxLength={128} required value={confirm} onChange={(event) => setConfirm(event.target.value)} />
        <button type="submit" className="guest-button" disabled={busy || code.length !== 6}>{busy ? "Saving…" : "Save password and sign in"}</button>
      </form>
      <p>No text? <button type="button" className="guest-link" onClick={() => { setRequestId(""); setCode(""); setError(""); }}>Ask for a new code</button>. If it still does not arrive, contact the council team.</p>
    </Page>
  );
}

function CodeStep({ challengeId, maskedPhone, onDone }: { challengeId: string; maskedPhone: string; onDone: () => void }) {
  const [code, setCode] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try { await post("/session/verify", { challengeId, code: code.trim() }); onDone(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "That code did not work."); }
    finally { setBusy(false); }
  };
  return (
    <Page title="Enter your code">
      <p>We have texted a 6-digit code to your mobile number {maskedPhone}. It works for 10 minutes.</p>
      <ErrorText text={error} />
      <form onSubmit={submit} className="guest-form">
        <CodeField value={code} onChange={setCode} />
        <button type="submit" className="guest-button" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Continue"}</button>
      </form>
      <p>No text? If it has expired, <a href="/guest/sign-in">sign in again</a> to get a new one.</p>
    </Page>
  );
}

export function GuestSignInPage({ onDone, notice = "", onRetrySignOut, authenticationBlocked = false }: { onDone: () => void; notice?: string; onRetrySignOut?: () => void; authenticationBlocked?: boolean }) {
  const [phone, setPhone] = useState(""), [password, setPassword] = useState(""), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [challenge, setChallenge] = useState<{ challengeId: string; maskedPhone: string } | null>(null);
  if (authenticationBlocked) return <Page title="Sign in">
    {notice ? <p role="status">{notice}</p> : null}
    <p>Finish signing out before signing in again.</p>
    {onRetrySignOut ? <button type="button" onClick={onRetrySignOut}>Finish signing out</button> : null}
  </Page>;
  if (challenge) return <CodeStep challengeId={challenge.challengeId} maskedPhone={challenge.maskedPhone} onDone={onDone} />;
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    try { setChallenge(await post<{ challengeId: string; maskedPhone: string }>("/session", { phone, password })); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "You could not be signed in."); }
    finally { setBusy(false); }
  };
  return (
    <Page title="Sign in">
      {notice ? <p className="guest-notice" role="status">{notice}</p> : null}
      {onRetrySignOut ? <button type="button" onClick={onRetrySignOut}>Finish signing out</button> : null}
      <h2 id="guest-returning-heading">Already set up? Sign in</h2>
      <ErrorText text={error} />
      <form onSubmit={submit} className="guest-form" aria-labelledby="guest-returning-heading">
        <PhoneField id="guest-phone" value={phone} onChange={setPhone} autoComplete="username" />
        <label htmlFor="guest-password">Password</label>
        <input id="guest-password" type="password" autoComplete="current-password" required maxLength={128} value={password} onChange={(event) => setPassword(event.target.value)} />
        <button type="submit" className="guest-button" disabled={busy}>{busy ? "Checking…" : "Continue"}</button>
      </form>
      <p>After this, we will text you a code. After 5 wrong passwords you must wait 15 minutes.</p>
      {/* Most visits are sign-ins, so the form comes first. A first-time
          caller has no password yet, so set-up is a clearly boxed choice
          directly below it, not a plain link. */}
      <section className="guest-first-time" aria-labelledby="guest-first-time-heading">
        <h2 id="guest-first-time-heading">First time here?</h2>
        <p>If the council has registered your mobile number, set up your sign-in first. We will text you a code and you will choose your own password.</p>
        <a className="guest-button" href="/guest/set-up">Set up your sign-in</a>
      </section>
    </Page>
  );
}

// Issue #1055: one voice call to the caller's own family member, through the
// same browser call as Carer Connect (carerConnectCall.ts). The server picks
// the Echo from the caller's Family link; this page never names it.
type CallOutcome = "in-progress" | "answered" | "unanswered" | "cancelled" | "unknown" | "unavailable" | "link-closed";
const STAGE_TEXT: Record<BridgeCallStage, string> = {
  microphone: "Getting your microphone ready…",
  calling: "Calling…",
  ringing: "Ringing. Your family member can choose whether to answer.",
  connected: "Connected. You can talk now.",
  unknown: "We cannot check the call right now. Trying again…"
};
// The calling service cannot tell a declined call from a missed one, so
// neither is claimed. No outcome says anything about the person's wellbeing.
export const OUTCOME_TEXT: Record<CallOutcome, string> = {
  "in-progress": "We could not tell how the call ended.",
  answered: "The call has ended.",
  unanswered: "Your family member did not answer.",
  cancelled: "You cancelled the call.",
  unknown: "We could not tell how the call ended.",
  unavailable: "The call could not be made. Try again later.",
  "link-closed": "The call was ended because your family calling has been paused or has ended."
};
const PROBLEM_TEXT: Record<BridgeCallProblem, string> = {
  "microphone-blocked": "Your browser blocked the microphone. Allow the microphone for this page, then try again.",
  // Family calls are voice only (#179 video is for care workers); these are never expected here.
  "camera-blocked": "Your browser blocked the camera or microphone. Allow them for this page, then try again.",
  "camera-missing": "We could not find a camera. Try again.",
  "lost-contact": "We lost contact with the call. Try again in a few minutes.",
  failed: "The call could not start. Try again."
};

function CallPanel({ onClosed }: { onClosed: (reason: ClosedReason) => void }) {
  const [stage, setStage] = useState<BridgeCallStage | "starting" | null>(null);
  const [connected, setConnected] = useState(false), [soundBlocked, setSoundBlocked] = useState(false);
  const [result, setResult] = useState(""), [error, setError] = useState("");
  const callRef = useRef<ReturnType<typeof startBridgeCall> | null>(null), audioRef = useRef<HTMLAudioElement>(null);
  const resultRef = useFocusOnChange<HTMLParagraphElement>(result);
  const mounted = useRef(true);
  // Leaving the page ends the call rather than leaving the Echo ringing.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; void callRef.current?.cancel(); };
  }, []);
  const start = async () => {
    setStage("starting"); setResult(""); setError(""); setConnected(false);
    try {
      const response = await fetch("/api/guest/calls", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode: "voice" }) });
      const body = await response.json().catch(() => ({}));
      if (!mounted.current) {
        if (response.ok && Number.isSafeInteger(body.call?.callId) && body.call.callId > 0) {
          void fetch(`/api/guest/calls/${body.call.callId}/cancel`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}", keepalive: true }).catch(() => undefined);
        }
        return;
      }
      if (response.status === 401) { setStage(null); onClosed((body.closedReason as ClosedReason) || "ended"); return; }
      if (!response.ok || !body.call) throw new Error(body.error || PROBLEM_TEXT.failed);
      callRef.current = startBridgeCall({
        base: `/api/guest/calls/${body.call.callId}`,
        iceServers: Array.isArray(body.media?.iceServers) ? body.media.iceServers : [],
        audio: audioRef.current,
        handlers: {
          onStage: (next) => { setStage(next); if (next === "connected") setConnected(true); },
          onSoundBlocked: setSoundBlocked,
          onFinished: ({ call, problem }) => {
            callRef.current = null; setStage(null); setConnected(false);
            setResult(problem ? PROBLEM_TEXT[problem] : OUTCOME_TEXT[(call?.outcome as CallOutcome) || "unknown"] || OUTCOME_TEXT.unknown);
          }
        }
      });
    } catch (reason) {
      if (!mounted.current) return;
      setStage(null);
      setError(reason instanceof Error ? reason.message : PROBLEM_TEXT.failed);
    }
  };
  const inCall = stage !== null;
  return (
    <>
      <audio ref={audioRef} autoPlay />
      {error ? <ErrorText text={error} /> : null}
      {result && !inCall ? <p className="guest-notice" role="status" ref={resultRef} tabIndex={-1}>{result}</p> : null}
      {inCall ? (
        <div className="guest-call">
          <p role="status" aria-live="polite">{stage === "starting" ? "Starting your call…" : STAGE_TEXT[stage]}</p>
          {soundBlocked ? (
            <div role="alert"><p>Your browser blocked the call sound.</p><button type="button" className="guest-button" onClick={() => void callRef.current?.playSound()}>Turn on sound</button></div>
          ) : null}
          <button type="button" className="guest-button guest-button-danger" disabled={stage === "starting"} onClick={() => void callRef.current?.cancel()}>{connected ? "End call" : "Cancel call"}</button>
        </div>
      ) : (
        <button type="button" className="guest-button" onClick={() => void start()}>{result ? "Call again" : "Voice call"}</button>
      )}
    </>
  );
}

export function CallerPage({ onSignOut, onPasswordEnded, onClosed }: { onSignOut: () => void; onPasswordEnded?: (message: string, complete?: boolean) => void; onClosed: (reason: ClosedReason) => void }) {
  const [portal, setPortal] = useState<CallerPortal | null>(null), [failed, setFailed] = useState(false), [attempt, setAttempt] = useState(0);
  const errorRef = useFocusOnChange<HTMLParagraphElement>(failed);
  const accountControls = <>
    {onPasswordEnded ? <PasswordChangeForm kind="guest" onEnded={onPasswordEnded} /> : null}
    <button type="button" className="guest-button guest-button-secondary" onClick={onSignOut}>Sign out</button>
  </>;
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    fetch("/api/guest/portal").then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (cancelled) return;
      if (response.ok) setPortal(body as CallerPortal);
      else if (response.status === 401) onClosed((body.closedReason as ClosedReason) || "ended");
      else setFailed(true);
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [attempt]);
  if (failed) {
    return (
      <Page title="Call your family member" accountControls={accountControls}>
        <p className="guest-error" role="alert" ref={errorRef} tabIndex={-1}>We could not load your calling page. Nothing has changed. Try again in a few minutes.</p>
        <button type="button" className="guest-button" onClick={() => setAttempt(attempt + 1)}>Try again</button>
        <p>{HELP}</p>
      </Page>
    );
  }
  if (!portal) return <Page title="Call your family member" accountControls={accountControls}><p role="status">Loading…</p></Page>;
  return (
    <Page title="Call your family member" accountControls={accountControls}>
      <section className="guest-panel" aria-labelledby="guest-calling-heading">
        <h2 id="guest-calling-heading">Calling</h2>
        {portal.calling.available && portal.calling.modes?.includes("voice") ? (
          <>
            <p>Your family member will need to answer on their Echo. Being able to call does not mean they are at home or able to answer.</p>
            <CallPanel onClosed={onClosed} />
          </>
        ) : (
          <>
            {portal.linkStatus !== "active" ? (
              <p className="guest-notice" role="status">Calling is not set up yet. The council still needs to finish setting it up.</p>
            ) : portal.calling.reason === "unavailable-now" ? (
              <p className="guest-notice" role="status">Calling is not available right now. Try again later.</p>
            ) : (
              <p className="guest-notice" role="status">Calling from this page is not ready yet. We will let you know when it is.</p>
            )}
            <p>When calling is ready, your family member will always need to answer on their Echo. Being able to call does not mean they are at home or able to answer.</p>
          </>
        )}
      </section>
      {portal.consentEndsOn ? (
        <section className="guest-panel" aria-labelledby="guest-consent-heading">
          <h2 id="guest-consent-heading">How long you can call</h2>
          <p>You can call until <strong>{longDate(portal.consentEndsOn)}</strong>. After that, the permission for you to call needs to be renewed.</p>
        </section>
      ) : null}
      <section className="guest-panel" aria-labelledby="guest-help-heading">
        <h2 id="guest-help-heading">Help</h2>
        <p>{HELP}</p>
      </section>
    </Page>
  );
}

export default function GuestApp() {
  const [path, setPath] = useState(window.location.pathname);
  const [session, setSession] = useState<Session | null>(null);
  const [notice, setNotice] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const [logoutPending, setLogoutPending] = useState(() => remoteSignOutPending("guest"));
  const generation = useRef(0);
  const go = (next: string) => { window.history.pushState(null, "", next); setPath(next); };
  const terminate = useCallback(() => {
    generation.current += 1;
    setLogoutPending(remoteSignOutPending("guest"));
    setSession({ configured: true, authenticated: false }); setNotice("Your sign-in has ended. Sign in again.");
    window.history.replaceState(null, "", "/guest/sign-in"); setPath("/guest/sign-in");
  }, []);
  useSessionTermination("guest", session?.authenticated ? session.expiresAt : undefined, terminate);
  const refresh = async (explicitSignIn = false) => {
    const current = ++generation.current;
    const result = await fetch("/api/guest/session").then((response) => {
      if (!response.ok) throw new Error("Session unavailable");
      return response.json();
    }).catch(() => ({ configured: false, authenticated: false }));
    if (current !== generation.current) return { configured: true, authenticated: false } as Session;
    if (explicitSignIn && result.authenticated) allowSignedIn("guest");
    const next = explicitSignIn && result.authenticated ? result : locallySignedOut("guest") ? { configured: result.configured, authenticated: false } : result;
    setSession(next);
    return next as Session;
  };
  useEffect(() => { void refresh(); const onPop = () => setPath(window.location.pathname); window.addEventListener("popstate", onPop); return () => window.removeEventListener("popstate", onPop); }, []);
  const authenticationGeneration = generation.current;
  const signedIn = async () => { if (logoutPending || authenticationGeneration !== generation.current) return; const result = await refresh(true); go(result.authenticated ? "/guest" : "/guest/sign-in"); };
  const signOut = async () => {
    markSignedOut("guest", true); terminate(); setLogoutPending(true); setSigningOut(true);
    setNotice("This page is signed out. Finishing sign out…");
    try {
      const response = await fetch("/api/guest/session", { method: "DELETE", signal: AbortSignal.timeout(10_000) });
      if (!response.ok) throw new Error("Sign out unavailable");
      markSignedOut("guest"); setLogoutPending(false);
      setNotice("You have signed out.");
    } catch {
      setNotice("This page is signed out. We could not finish signing out of the service. Reconnect before signing in again.");
    } finally { setSigningOut(false); }
  };

  if (signingOut) return <Page title="Sign in"><p role="status">{notice}</p></Page>;
  if (!session) return <Page title="Loading"><p role="status">Loading…</p></Page>;
  if (!session.configured) return <Page title="Family calling is not available"><p>Family sign-in is not available at the moment. Try again later.</p></Page>;
  // /guest/invite is kept for any earlier link and opens set-up.
  if (!logoutPending && (path.startsWith("/guest/set-up") || path.startsWith("/guest/invite"))) return <GuestSetUpPage key={authenticationGeneration} onDone={signedIn} />;
  if (session.authenticated && session.guest) return <CallerPage onSignOut={signOut} onPasswordEnded={(message, complete) => { markSignedOut("guest", !complete); terminate(); setLogoutPending(!complete); setNotice(message); }} onClosed={(closedReason) => { setSession({ configured: true, authenticated: false, closedReason }); go("/guest/sign-in"); }} />;
  return <GuestSignInPage key={authenticationGeneration} onDone={signedIn} authenticationBlocked={logoutPending} onRetrySignOut={logoutPending ? signOut : undefined} notice={notice || (session.closedReason ? CLOSED_TEXT[session.closedReason] : "")} />;
}
